import { fechaHora, useTraduccion } from '@tallerya/i18n';
import { Ayuda, AvisoError, Boton, Chip } from '@tallerya/ui';
import { useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router';
import { useTituloPantalla } from '../../layout/titulo';
import { usePermisos } from '../../permisos/usePermiso';
import { AccionesDeEstado } from './AccionesDeEstado';
import { useCabeceraOrden, type CabeceraOrden } from './datos';
import { descripcionDe } from './ElegirVehiculo';
import { EditarCabecera } from './EditarCabecera';
import { SeccionFotos } from './SeccionFotos';
import { SeccionGastos } from './SeccionGastos';
import { PanelHistorial } from './PanelHistorial';
import { SeccionTrabajo } from './SeccionTrabajo';

// La ficha de la orden (#131): la cabecera de «Órdenes de servicio - Detalle»
// (`190:9042`) —folio, estado, vehículo, cliente, asignado, apertura, entrega
// prometida y falla reportada—, editable con «Editar».
//
// **Es el esqueleto del tramo 3.** Debajo de la cabecera van colgando sus
// secciones, cada una con su issue: Trabajo (#133, en `SeccionTrabajo`), montos
// (#137), Fotos (#140, en `SeccionFotos`), Historial (#141, en `PanelHistorial`, que se abre a la derecha: #193) y
// Gastos (#143, en `SeccionGastos`). Las que faltan no dejan cajas vacías esperándolas. Cambiar de estado (#135) vive en la cabecera, en
// `AccionesDeEstado`.
//
// Lo que no sigue al Figma, a propósito:
//
//   - «Volver a Órdenes de servicio» vuelve con «Atrás» si se llegó desde el
//     listado (#139): así la encuentra con los filtros que tenía, que viven en
//     la URL. La página no: se vuelve a la primera, como en Clientes.
//   - Sin folio —una orden abierta sin señal en la fase 4— dice «Pendiente».

export function FichaOrden() {
  const { t } = useTraduccion();
  const navegar = useNavigate();
  const { id } = useParams();
  const consulta = useCabeceraOrden(id);
  useTituloPantalla(consulta.data?.orderNumber ?? t('admin.ordenes.titulo'));

  if (consulta.isPending) return <p className="entrada">{t('app.cargando')}</p>;

  if (consulta.isError || consulta.data === null) {
    const noExiste = consulta.data === null;
    return (
      <section className="aviso">
        <h2>{noExiste ? t('admin.ordenes.noExiste.titulo') : t('ui.listado.error.titulo')}</h2>
        <p>{noExiste ? t('admin.ordenes.noExiste.texto') : t('ui.listado.error.texto')}</p>
        <Boton variante="secundario" onClick={() => void navegar('/ordenes')}>
          {t('admin.ordenes.volver')}
        </Boton>
      </section>
    );
  }

  // La clave reinicia la cabecera al pasar a otra orden —el enlace del retrabajo—:
  // el aviso o una ventana a medias de una no aparecen en la otra.
  return <Cabecera key={String(consulta.data.id)} orden={consulta.data} />;
}

function Cabecera({ orden }: { orden: CabeceraOrden }) {
  const { t, idioma } = useTraduccion();
  const { puede } = usePermisos();
  const navegar = useNavigate();
  const desdeListado = (useLocation().state as { desdeListado?: boolean } | null)?.desdeListado ?? false;
  const [editando, setEditando] = useState(false);
  // El historial empieza cerrado y no se pide hasta abrirlo (#193).
  const [viendoHistorial, setViendoHistorial] = useState(false);
  // Lo que la orden rechazó. Se quita con cualquier cambio que salga bien —un
  // cambio de estado o editar la cabecera—: «falta el cliente» no sigue en pantalla
  // con el cliente ya puesto.
  const [problema, setProblema] = useState<string | null>(null);

  const vehiculo = orden.assetId
    ? [
        descripcionDe({ brandName: orden.assetBrandName, modelName: orden.assetModelName, year: orden.assetYear }) ||
          orden.assetTypeName,
        orden.assetIdentifier,
      ]
        .filter(Boolean)
        .join(' · ')
    : null;
  const cliente = orden.customerId ? [orden.customerName, orden.customerPhone].filter(Boolean).join(' · ') : null;
  const abierta = [
    fechaHora(orden.openedAt, idioma),
    orden.createdByName && t('admin.ordenes.ficha.por', { persona: orden.createdByName }),
  ]
    .filter(Boolean)
    .join(' · ');

  // Solo los datos que la orden tiene, salvo los dos que siempre importan: un
  // «Sin asignar» o un «Sin cliente» también es información.
  const datos: [string, string][] = [
    ...(vehiculo ? [[t('admin.ordenes.ficha.vehiculo'), vehiculo] as [string, string]] : []),
    [t('admin.ordenes.ficha.cliente'), cliente ?? t('admin.ordenes.ficha.sinCliente')],
    [t('admin.ordenes.ficha.asignadoA'), orden.assignedToName ?? t('admin.ordenes.sinAsignar')],
    [t('admin.ordenes.ficha.abierta'), abierta],
    ...(orden.promisedAt
      ? [[t('admin.ordenes.entregaPrometida'), fechaHora(orden.promisedAt, idioma)] as [string, string]]
      : []),
    ...(orden.externalReference
      ? [[t('admin.ordenes.referenciaExterna'), orden.externalReference] as [string, string]]
      : []),
  ];

  const folio = orden.orderNumber ?? t('admin.ordenes.ficha.sinFolio');

  return (
    <>
      <Link
        className="orden__volver"
        to="/ordenes"
        onClick={(e) => {
          if (!desdeListado) return;
          e.preventDefault();
          void navegar(-1);
        }}
      >
        ← {t('admin.ordenes.volver')}
      </Link>

      <section className="orden__cabecera">
        <header className="orden__titulo">
          <div>
            <h1>
              {folio}
              <Ayuda tema={folio} texto={t('admin.ordenes.ficha.info')} tono="titulo" />
              {/* El color del estado, el que eligió el taller: como en la ficha del cliente. */}
              <Chip>
                {orden.statusColor && (
                  <span className="ficha__punto" style={{ background: orden.statusColor }} aria-hidden="true" />
                )}
                {orden.statusName}
              </Chip>
            </h1>
            <p className="tipos__resumen">{orden.locationName}</p>
          </div>
          <div className="tipos__acciones">
            {/* El historial pide `order.view`, lo mismo que esta ficha: lo ve todo el que llega. */}
            <Boton variante="secundario" onClick={() => setViendoHistorial(true)}>
              {t('admin.historial.titulo')}
            </Boton>
            {/* Editar, y mover de estado, son de `order.edit`: quien solo ve órdenes
                no tiene los botones. Entregar, anular y eliminar piden lo suyo
                adentro de `AccionesDeEstado`. */}
            {puede('order.edit') && (
              <>
                <Boton variante="secundario" onClick={() => setEditando(true)}>
                  {t('admin.ordenes.ficha.editar')}
                </Boton>
                <AccionesDeEstado
                  orden={orden}
                  onProblema={setProblema}
                />
              </>
            )}
          </div>
        </header>

        {/* Lo que la orden rechazó, todo junto: «para entregarla falta el cliente
            y el folio» en un solo aviso. Se va con el próximo cambio que salga bien. */}
        {problema && <AvisoError aviso={{ titulo: t('admin.ordenes.estado.noSePudo'), texto: problema }} />}

        <dl className="orden__datos">
          {datos.map(([etiqueta, valor]) => (
            <div key={etiqueta}>
              <dt>{etiqueta}</dt>
              <dd>{valor}</dd>
            </div>
          ))}
        </dl>

        {orden.parentOrderId && (
          <p className="orden__retrabajo">
            {t('admin.ordenes.ficha.retrabajoDe')}{' '}
            <Link to={`/ordenes/${String(orden.parentOrderId)}`}>
              {orden.parentOrderNumber ?? t('admin.ordenes.ficha.sinFolio')}
            </Link>
          </p>
        )}

        {orden.reportedIssue && (
          <div className="orden__falla">
            <p className="orden__etiqueta">{t('admin.ordenes.fallaReportada')}</p>
            <p>«{orden.reportedIssue}»</p>
          </div>
        )}

        {orden.notes && (
          <div className="orden__falla">
            <p className="orden__etiqueta">{t('admin.ordenes.notas')}</p>
            <p>{orden.notes}</p>
          </div>
        )}
      </section>

      <SeccionTrabajo orden={orden} />
      <SeccionGastos orden={orden} />
      <SeccionFotos orden={orden} />
      <PanelHistorial orden={orden} abierto={viendoHistorial} onCerrar={() => setViendoHistorial(false)} />

      <EditarCabecera
        orden={orden}
        abierta={editando}
        onCerrar={() => setEditando(false)}
        onGuardada={() => setProblema(null)}
      />
    </>
  );
}
